import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";
import type {
  AgentTokenDto,
  BulkStageRequest,
  BulkStageResult,
  CompanyDetailResponse,
  CompanyListResponse,
  CompanyStage,
  ImportDto,
  ImportKind,
  PipelineContextResponse,
  ReferenceClientsResponse,
  ReviewDecisionRequest,
  ReviewQueueResponse,
  RunDetailResponse,
  RunSummary,
  SequenceDetailResponse,
  SequenceStatus,
  StrategyDetailResponse,
  StrategyListItem,
  SuppressionEntryDto,
  UpdateCompanyRequest,
} from "@pinpoint/shared";
import { apiFetch } from "./api";

/**
 * Data access for the pipeline screens.
 *
 * Mutations invalidate broadly rather than surgically patching the cache: a
 * stage change moves a company between filtered lists, changes the funnel
 * counts, and adds a timeline entry, so the set of things it invalidates is
 * "most of the app" anyway. Refetching is cheap here and correctness is not.
 */

export interface CompanyFilters {
  query?: string;
  stage?: CompanyStage[];
  industry?: string;
  sizeBand?: string;
  sourceType?: "cold" | "warm";
  sourcingStrategyKey?: string;
  minScore?: number;
  hasContacts?: boolean;
  staleEnrichment?: boolean;
  sort?: "score" | "recent" | "name";
  limit?: number;
  offset?: number;
}

function toSearchParams(filters: CompanyFilters): string {
  const params = new URLSearchParams();
  if (filters.query) params.set("query", filters.query);
  for (const stage of filters.stage ?? []) params.append("stage", stage);
  if (filters.industry) params.set("industry", filters.industry);
  if (filters.sizeBand) params.set("size_band", filters.sizeBand);
  if (filters.sourceType) params.set("source_type", filters.sourceType);
  if (filters.sourcingStrategyKey) {
    params.set("sourcing_strategy_key", filters.sourcingStrategyKey);
  }
  if (filters.minScore !== undefined) {
    params.set("min_score", String(filters.minScore));
  }
  if (filters.hasContacts !== undefined) {
    params.set("has_contacts", String(filters.hasContacts));
  }
  if (filters.staleEnrichment) params.set("stale_enrichment", "true");
  if (filters.sort) params.set("sort", filters.sort);
  if (filters.limit !== undefined) params.set("limit", String(filters.limit));
  if (filters.offset) params.set("offset", String(filters.offset));
  return params.toString();
}

export const pipelineKeys = {
  context: ["pipeline", "context"] as const,
  companies: (filters: CompanyFilters) =>
    ["pipeline", "companies", filters] as const,
  company: (id: string) => ["pipeline", "company", id] as const,
  runs: ["pipeline", "runs"] as const,
};

export function usePipelineContext(
  options?: Partial<UseQueryOptions<PipelineContextResponse>>,
) {
  return useQuery({
    queryKey: pipelineKeys.context,
    queryFn: () => apiFetch<PipelineContextResponse>("/api/me/context"),
    ...options,
  });
}

export function useCompanies(filters: CompanyFilters) {
  return useQuery({
    queryKey: pipelineKeys.companies(filters),
    queryFn: () =>
      apiFetch<CompanyListResponse>(`/api/me/companies?${toSearchParams(filters)}`),
    // Keeps the previous page on screen while a filter change loads, so the
    // table doesn't collapse to a skeleton on every keystroke.
    placeholderData: (previous) => previous,
  });
}

export function useCompany(id: string | null) {
  return useQuery({
    queryKey: pipelineKeys.company(id ?? ""),
    queryFn: () => apiFetch<CompanyDetailResponse>(`/api/me/companies/${id}`),
    enabled: Boolean(id),
  });
}

export function useRuns() {
  return useQuery({
    queryKey: pipelineKeys.runs,
    queryFn: () => apiFetch<{ runs: RunSummary[] }>("/api/me/runs"),
  });
}

function useInvalidatePipeline() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ["pipeline"] });
}

export function useUpdateCompany(id: string) {
  const invalidate = useInvalidatePipeline();

  return useMutation({
    mutationFn: (patch: UpdateCompanyRequest) =>
      apiFetch<{ id: string; stage: CompanyStage; score: number | null }>(
        `/api/me/companies/${id}`,
        { method: "PATCH", body: JSON.stringify(patch) },
      ),
    onSuccess: invalidate,
  });
}

export interface BulkStageResponse {
  results: BulkStageResult[];
  updated: number;
  failed: number;
}

export function useBulkStage() {
  const invalidate = useInvalidatePipeline();

  return useMutation({
    mutationFn: (body: BulkStageRequest) =>
      apiFetch<BulkStageResponse>("/api/me/companies/bulk-stage", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: invalidate,
  });
}

// --- Review queue -----------------------------------------------------------

export function useReviewQueue(strategyKey?: string) {
  return useQuery({
    queryKey: ["pipeline", "review", "queue", strategyKey ?? null],
    queryFn: () =>
      apiFetch<ReviewQueueResponse>(
        `/api/me/review/queue${strategyKey ? `?strategy_key=${encodeURIComponent(strategyKey)}` : ""}`,
      ),
  });
}

export function useSequence(id: string | null) {
  return useQuery({
    queryKey: ["pipeline", "review", "sequence", id],
    queryFn: () => apiFetch<SequenceDetailResponse>(`/api/me/review/sequences/${id}`),
    enabled: Boolean(id),
  });
}

export function useReviewSequence() {
  const invalidate = useInvalidatePipeline();

  return useMutation({
    mutationFn: ({ id, ...decision }: { id: string } & ReviewDecisionRequest) =>
      apiFetch<{ id: string; status: SequenceStatus }>(
        `/api/me/review/sequences/${id}/review`,
        { method: "POST", body: JSON.stringify(decision) },
      ),
    onSuccess: invalidate,
  });
}

// --- Strategies, runs, settings ---------------------------------------------

export function useStrategies() {
  return useQuery({
    queryKey: ["pipeline", "strategies"],
    queryFn: () => apiFetch<{ strategies: StrategyListItem[] }>("/api/me/strategies"),
  });
}

export function useStrategy(key: string | null) {
  return useQuery({
    queryKey: ["pipeline", "strategy", key],
    queryFn: () =>
      apiFetch<StrategyDetailResponse>(`/api/me/strategies/${key}`),
    enabled: Boolean(key),
  });
}

export function useSaveStrategyVersion(key: string) {
  const invalidate = useInvalidatePipeline();
  return useMutation({
    mutationFn: (body: { playbook_md: string; change_note: string }) =>
      apiFetch<{ version: number; status: string }>(
        `/api/me/strategies/${key}/versions`,
        { method: "POST", body: JSON.stringify(body) },
      ),
    onSuccess: invalidate,
  });
}

export function useSetVersionStatus(key: string) {
  const invalidate = useInvalidatePipeline();
  return useMutation({
    mutationFn: (body: { version: number; status: string }) =>
      apiFetch<{ version: number; status: string }>(
        `/api/me/strategies/${key}/status`,
        { method: "POST", body: JSON.stringify(body) },
      ),
    onSuccess: invalidate,
  });
}

export function useRun(id: string | null) {
  return useQuery({
    queryKey: ["pipeline", "run", id],
    queryFn: () => apiFetch<RunDetailResponse>(`/api/me/runs/${id}`),
    enabled: Boolean(id),
  });
}

export function useAgentTokens() {
  return useQuery({
    queryKey: ["pipeline", "tokens"],
    queryFn: () =>
      apiFetch<{ tokens: AgentTokenDto[] }>("/api/me/settings/agent-tokens"),
  });
}

export function useCreateAgentToken() {
  const invalidate = useInvalidatePipeline();
  return useMutation({
    mutationFn: (name: string) =>
      apiFetch<{ id: string; name: string; token: string }>(
        "/api/me/settings/agent-tokens",
        { method: "POST", body: JSON.stringify({ name }) },
      ),
    onSuccess: invalidate,
  });
}

export function useRevokeAgentToken() {
  const invalidate = useInvalidatePipeline();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ ok: true }>(`/api/me/settings/agent-tokens/${id}`, {
        method: "DELETE",
      }),
    onSuccess: invalidate,
  });
}

export function useSuppression(query?: string) {
  return useQuery({
    queryKey: ["pipeline", "suppression", query ?? null],
    queryFn: () =>
      apiFetch<{ entries: SuppressionEntryDto[]; total: number }>(
        `/api/me/settings/suppression${query ? `?query=${encodeURIComponent(query)}` : ""}`,
      ),
  });
}

export function useAddSuppression() {
  const invalidate = useInvalidatePipeline();
  return useMutation({
    mutationFn: (body: { kind: "email" | "domain"; value: string }) =>
      apiFetch<{ ok: true }>("/api/me/settings/suppression", {
        method: "POST",
        body: JSON.stringify({ ...body, reason: "manual" }),
      }),
    onSuccess: invalidate,
  });
}

export function useRemoveSuppression() {
  const invalidate = useInvalidatePipeline();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ ok: true }>(`/api/me/settings/suppression/${id}`, {
        method: "DELETE",
      }),
    onSuccess: invalidate,
  });
}

export function useAppSettings() {
  return useQuery({
    queryKey: ["pipeline", "config"],
    queryFn: () =>
      apiFetch<{ settings: { key: string; value: unknown; updatedAt: string }[] }>(
        "/api/me/settings/config",
      ),
  });
}

export function useUpdateAppSetting() {
  const invalidate = useInvalidatePipeline();
  return useMutation({
    mutationFn: (body: { key: string; value: unknown }) =>
      apiFetch<{ ok: true }>("/api/me/settings/config", {
        method: "PUT",
        body: JSON.stringify(body),
      }),
    onSuccess: invalidate,
  });
}

// --- Pasted imports ---------------------------------------------------------

export function useImports() {
  return useQuery({
    queryKey: ["pipeline", "imports"],
    queryFn: () => apiFetch<{ imports: ImportDto[] }>("/api/me/imports"),
    // A pasted list is processed by an agent out of band, so the screen polls
    // rather than leaving someone staring at a stale "waiting" card.
    refetchInterval: (query) =>
      (query.state.data?.imports ?? []).some(
        (i) => i.status === "pending" || i.status === "processing",
      )
        ? 10_000
        : false,
  });
}

export function useReferenceClients() {
  return useQuery({
    queryKey: ["pipeline", "reference-clients"],
    queryFn: () =>
      apiFetch<ReferenceClientsResponse>("/api/me/reference-clients"),
    refetchInterval: (query) =>
      (query.state.data?.pendingImports ?? []).length > 0 ? 10_000 : false,
  });
}

export function useCreateImport() {
  const invalidate = useInvalidatePipeline();
  return useMutation({
    mutationFn: (body: {
      kind: ImportKind;
      raw_text: string;
      source_note?: string;
    }) =>
      apiFetch<{ id: string; status: string }>("/api/me/imports", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: invalidate,
  });
}

export function useStopRun() {
  const invalidate = useInvalidatePipeline();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      apiFetch<{ id: string; status: string }>(`/api/me/runs/${id}/stop`, {
        method: "POST",
        body: JSON.stringify({ reason }),
      }),
    onSuccess: invalidate,
  });
}
